/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * SFCC's platform-default locale sentinel. Ashley-US defines only this locale on
 * the instance, so it is what `i18n.language` resolves to at runtime.
 */
const SFCC_DEFAULT_LOCALE = 'default';

/**
 * BCP-47 tag Ashley formats currency, numbers, and dates with. Ashley is a
 * US-only single-locale storefront.
 */
export const FORMATTING_LOCALE = 'en-US';

/**
 * HTML `lang` value for Ashley-US. Must be a real locale directory under
 * `src/locales/` (`en-US`), not `en` and not the SFCC `default` sentinel:
 * client i18next initializes from `document.documentElement.lang` and loads
 * `@/locales/${language}/index.ts`.
 */
export const HTML_LANG = FORMATTING_LOCALE;

/**
 * Maps an i18next language onto a BCP-47 tag safe to hand to `Intl`.
 *
 * `default` is a valid BCP-47 *language subtag* but matches no actual locale, so
 * `Intl` silently resolves it to the runtime's own default rather than throwing.
 * On the server that happens to be `en-US`; in the browser it is the shopper's
 * browser locale, so a US shopper on a de-DE browser would otherwise see
 * `1.234,50 $` and `6. August` instead of `$1,234.50` and `Aug 6`.
 */
export function resolveFormattingLocale(locale: string | undefined): string {
    return !locale || locale === SFCC_DEFAULT_LOCALE ? FORMATTING_LOCALE : locale;
}

/**
 * Maps an i18next language onto a value safe to put on `<html lang>`.
 *
 * SFCC Ashley-US only defines the platform `default` locale, so `i18n.language`
 * resolves to that sentinel at runtime. `default` is not a language browsers or
 * assistive tech understand. `en` is a valid HTML lang but is not a locale
 * directory — emitting it makes client i18next try to import `@/locales/en/`
 * and every string renders as its raw key. `en-US` is both a real BCP-47 tag
 * and a real translation bundle. Real locale codes pass through.
 */
export function resolveHtmlLang(locale: string | undefined): string {
    return !locale || locale === SFCC_DEFAULT_LOCALE ? HTML_LANG : locale;
}
